/**
 * Wave B2 (decision 0057): what keeps Lea or the work from moving, in the order to fix it, derived from Lea's presence,
 * her last turn and the runners (no cloud change). One banner (`SetupStrip`) shows it above the composer and on the
 * Overview, Team and Home; the chat hides a `setup` notice while the banner covers it and renders `info` notices from
 * their code, never from the stored text. Pure.
 *
 * Wave O1 (decision 0060): the problems, their sentence and `blocksLead` live in core (`@minga/core` setup), so the
 * cloud's `read_project.setup` and this banner say the same thing; they are re-exported here. The web keeps what only
 * the page knows: `setupInputOf` (the project context), the fix buttons and the chat notices.
 */
import type { LeadReach, ProjectState, ReachRunner, Run, SetupInput, SetupProblem } from "@minga/core"
import {
  cliName,
  cliOfMind,
  isOpenProposal,
  leadReach,
  resolveRouting,
  routedFamily,
  turnRunsNatively,
  workspaceKeyOf,
} from "@minga/core"
import type { PlatformRunnerState, RunnerListItemV1, WorkspaceRole } from "@minga/protocol"
import type { ProjectContextValue } from "@/features/project/project-context"
import { parseManifest } from "@/features/runners/manifest"
import { clockTime } from "@/lib/time"
import type { Fix } from "./stops"

export type { LeadReach, SetupInput, SetupProblem, WorkspaceKeyState } from "@minga/core"
export { blocksLead, cliOfMind, leadReach, setupProblems, setupSentence, workspaceKeyOf } from "@minga/core"

/**
 * Polish 2026-10-23 §5: who reads the banner, so "Lea's runner is offline" names whose machine it is and offers what
 * this person can do. `runnerOwner`: a runner's owner (null when unknown).
 */
export interface SetupViewer {
  readonly userId: string
  readonly runnerOwner: (runnerId: string) => { readonly userId: string; readonly name: string | null } | null
  /** Decision 0166 §2: a person's first name ("Mira"), or null when unknown. */
  readonly nameOf?: ((userId: string) => string | null) | undefined
}

/**
 * The problems as this viewer reads them: an offline runner names its owner ("you", "Jannik"); decision 0166 §2: a
 * login only someone else could share names them ("Mira").
 */
export const forViewer = (
  problems: ReadonlyArray<SetupProblem>,
  viewer: SetupViewer | undefined,
): ReadonlyArray<SetupProblem> =>
  viewer
    ? problems.map((p) => {
        if (p._tag === "no_cli" && p.lockedTo !== undefined) {
          const name = viewer.nameOf?.(p.lockedTo) ?? null
          return name ? { ...p, owner: name } : p
        }
        if (p._tag !== "runner_offline") return p
        const owner = viewer.runnerOwner(p.runnerId)
        if (!owner) return p
        const word = owner.userId === viewer.userId ? "you" : owner.name
        return word ? { ...p, owner: word } : p
      })
    : problems

/**
 * Polish 2026-10-23 §5: the line after the sentence, for an offline runner: its owner starts it; anyone else learns the
 * lead answers once it is back and whom to ask. Null otherwise.
 */
export const setupNote = (
  problems: ReadonlyArray<SetupProblem>,
  leadName: string,
  role: WorkspaceRole,
): string | null => {
  const off = problems.find((p) => p._tag === "runner_offline")
  if (off?._tag !== "runner_offline" || off.owner === undefined) return null
  if (off.owner === "you") return `Start it on its machine; ${leadName} picks up then.`
  const who = off.owner
  return role === "owner" || role === "admin"
    ? `${leadName} answers once it's back: ask ${who} to start it, or run ${leadName} on the Platform Runner.`
    : `${leadName} answers once it's back: ask ${who} to start it.`
}

/**
 * Decision 0166 §2: the open share card (0128 addendum) asking `ownerId` to share their login so `askerId`'s message
 * can run, or null.
 */
export const openShareCardFor = (s: ProjectState, ownerId: string, askerId: string): string | null =>
  s.proposals.find(
    (p) =>
      isOpenProposal(p) &&
      p.ownerId === ownerId &&
      p.intentKey.startsWith("share_login:") &&
      !p.intentKey.startsWith("share_login:none:") &&
      p.intentKey.endsWith(`:${askerId}`),
  )?.id ?? null

/** Decision 0166 §2: what the page knows for the fixes: the lead (its settings) and the open share cards. */
export interface SetupFixContext {
  readonly lead?: { readonly projectId: string; readonly handle: string } | undefined
  /** The open share card asking `ownerId` to share their login for this viewer's message, if any. */
  readonly shareCardFor?: ((ownerId: string) => string | null) | undefined
}

/**
 * The fixes as buttons, at most two, in order: Pair a runner (or open Lea's runner), Add a key. Polish 2026-10-23 §5:
 * someone else's offline runner is not this viewer's to open; owners and admins get the Platform Runner instead.
 *
 * Decision 0166 §2: when nothing the viewer may use runs the lead (`no_cli` found before a turn waits, or without a
 * pinned runner): a member gets Connect your subscription and Ask <owner> to share (decision 0168: only Ask when the
 * lead is pinned to that person's machine, where no subscription of the member's would run it); an owner or admin gets Use the
 * Platform Runner and Connect a subscription. Decision 0166 §3.6: when only the viewer's own choice of subscriptions in this
 * project keeps it (`notChosen`), Your subscriptions here (Project settings › Members).
 */
export const setupFixes = (
  problems: ReadonlyArray<SetupProblem>,
  role: WorkspaceRole,
  ctx: SetupFixContext = {},
): ReadonlyArray<Fix> => {
  const out: Fix[] = []
  const offline = problems.find((p) => p._tag === "runner_offline")
  const signedOut = problems.find((p) => p._tag === "cli_signed_out")
  const noCli = problems.find((p) => p._tag === "no_cli")
  const manager = role === "owner" || role === "admin"
  const noAccess =
    noCli?._tag === "no_cli" &&
    (noCli.lockedTo !== undefined || noCli.reached === true || noCli.runnerId === null)
  if (noCli?._tag === "no_cli" && noCli.notChosen === true)
    // decision 0166 §3.6: the viewer's own choice of subscriptions here leaves the lead's CLI out
    out.push({ _tag: "choose_subscriptions" })
  else if (noAccess && noCli?._tag === "no_cli") {
    // re-walk 2026-10-28: a lead already on the Platform Runner needs a subscription for its CLI, not the runner
    if (manager && noCli.hosted === true) out.push({ _tag: "connect_subscription", cli: noCli.cli })
    else if (manager) out.push({ _tag: "platform_runner", lead: ctx.lead }, { _tag: "connect_subscription" })
    else {
      // decision 0168: a lead pinned to someone else's machine runs there whatever the member connects: only the
      // owner's share helps, so Connect is left out; otherwise it stays first
      const pinnedElsewhere = noCli.lockedOn === "machine" && noCli.runnerId !== null && noCli.hosted !== true
      if (!pinnedElsewhere) out.push({ _tag: "connect_subscription" })
      if (noCli.lockedTo !== undefined)
        out.push({
          _tag: "ask_share",
          ownerId: noCli.lockedTo,
          proposalId: ctx.shareCardFor?.(noCli.lockedTo) ?? undefined,
        })
    }
  } else if (problems.some((p) => p._tag === "no_runner")) out.push({ _tag: "pair_runner" })
  else if (offline?._tag === "runner_offline")
    out.push(
      offline.owner === undefined || offline.owner === "you"
        ? { _tag: "open_runner", runnerId: offline.runnerId }
        : role === "owner" || role === "admin"
          ? { _tag: "platform_runner" }
          : { _tag: "none" },
    )
  else if (signedOut?._tag === "cli_signed_out" && signedOut.runnerId)
    out.push({ _tag: "open_runner", runnerId: signedOut.runnerId })
  else if (noCli?._tag === "no_cli" && noCli.runnerId)
    out.push({ _tag: "open_runner", runnerId: noCli.runnerId })
  if (problems.some((p) => p._tag === "no_key" || p._tag === "key_refused" || p._tag === "no_credit"))
    out.push({ _tag: "add_key" })
  return out.filter((f) => f._tag !== "none").slice(0, 2)
}

/**
 * A fix's button label for this viewer ("Ask an owner to add a key": members can't add one). `nameOf`: a person's first
 * name, for "Ask Mira to share".
 */
export const fixLabel = (
  fix: Fix,
  role: WorkspaceRole,
  nameOf: (userId: string) => string | null = () => null,
): string => {
  switch (fix._tag) {
    case "pair_runner":
      return "Pair a runner"
    case "platform_runner":
      return "Use the Platform Runner"
    case "connect_subscription":
      if (fix.cli) return `Connect ${fix.cli}`
      return role === "member" ? "Connect your subscription" : "Connect a subscription"
    case "ask_share":
      return `Ask ${nameOf(fix.ownerId) ?? "its owner"} to share`
    case "choose_subscriptions":
      return "Your subscriptions here"
    case "add_key":
      return role === "member" ? "Ask an owner to add a key" : "Add a key"
    case "open_runner":
      return "Open the runner"
    case "update_runner":
      return "Update the runner"
    case "retry":
      return "Retry"
    case "details":
      return "Details"
    case "wait":
    case "none":
      return ""
  }
}

const isLiveTurnOf = (r: Run, agentId: string) => r.kind === "turn" && r.agentId === agentId

/**
 * Decision 0166 §2: the runners as `leadReach` reads them. The Platform Runner comes from its own state (its
 * subscriptions are its profiles, whose they are and whether they are shared; it starts on demand); every other runner
 * from the list (online, its enabled and signed-in profiles, Just me / The team and per-profile access). Decision 0168:
 * someone else's personal machine arrives redacted with `sharedProfiles` (the logins the viewer may use) instead of a
 * manifest.
 */
export const reachRunnersOf = (
  runners: ReadonlyArray<RunnerListItemV1>,
  platform: PlatformRunnerState | null,
): ReadonlyArray<ReachRunner> => {
  const hostedId = platform?.configured ? platform.runnerId : null
  const out: ReachRunner[] = runners
    .filter((r) => r.runnerId !== hostedId)
    .map((r) => ({
      id: r.runnerId as never,
      ownerId: r.ownerUserId,
      serves: r.serves ?? "owner",
      ...(r.profileAccess ? { profileAccess: r.profileAccess as never } : {}),
      available: r.status === "online",
      // decision 0168: someone else's personal machine lists only the logins shared with the viewer; its owner's own
      // ones are hidden, and one of them could still run the lead if shared
      ...(r.redacted === true ? { hidden: true } : {}),
      profiles:
        r.redacted === true
          ? (r.sharedProfiles ?? []).map((p) => ({ id: p.id, agent: p.agent, ready: p.ready }))
          : (parseManifest(r.manifest)?.profiles ?? []).map((p) => ({
              id: p.id,
              agent: p.agent,
              ready: p.enabled && p.account.loggedIn,
            })),
    }))
  if (platform && hostedId) {
    const ownerOf = (s: PlatformRunnerState["subscriptions"][number]) =>
      (s.ownerUserId ?? platform.ownerUserId ?? "") as string
    out.push({
      id: hostedId as never,
      ownerId: (platform.ownerUserId ?? "") as never,
      serves: "team",
      hosted: true,
      available: true,
      profileAccess: Object.fromEntries(
        platform.subscriptions.map((s) => [
          s.id,
          {
            ownerId: ownerOf(s),
            scope: s.scope ?? "personal",
            // decision 0166 §3.2: shared in some projects only
            ...(s.scope === "projects" ? { projectIds: [...(s.projectIds ?? [])] } : {}),
          },
        ]),
      ) as never,
      profiles: platform.subscriptions.map((s) => ({
        id: s.id,
        agent: s.agent,
        ready: s.expiresAt === null || s.expiresAt > Date.now(),
      })),
    })
  }
  return out
}

/** Inputs from the project page: the context's model, state, runners (runnersLoaded) and runs. */
export const setupInputOf = (
  ctx: Pick<ProjectContextValue, "model" | "state" | "runners" | "runnersLoaded" | "modelKeys"> &
    Partial<Pick<ProjectContextValue, "userId" | "platform" | "projectId" | "subscriptionChoice">>,
): SetupInput => {
  const leadView = ctx.model.agents.find((a) => a.role === "lead" && !a.ghost) ?? null
  const core = leadView ? ctx.state.agents.find((a) => a.id === leadView.id) : undefined
  const native = core !== undefined && turnRunsNatively(ctx.state, core)
  const route = resolveRouting(ctx.state.project?.routing, { kind: "turn" })
  // decision 0118: the lead's own CLI when the route allows it, else the route's first
  const nativeAgent = routedFamily(route, core?.mind._tag === "runner" ? core.mind.agent : null) ?? undefined
  const lastTurn = leadView ? lastEndedTurn(ctx.state, leadView.id) : null
  const runnerId =
    (route.required ? route.rule?.runnerId : null) ??
    (core?.mind._tag === "runner" ? (core.mind.requires.runner ?? null) : null)
  // re-walk 2026-10-28: the Platform Runner starts on demand (listed offline between tasks)
  const hostedId = ctx.platform?.configured ? (ctx.platform.runnerId ?? null) : null
  const hosted = runnerId !== null && runnerId === hostedId
  // decision 0166 §2: whether a turn this viewer asks could start now, before any turn waits
  const agent = native ? nativeAgent : core?.mind._tag === "runner" ? core.mind.agent : undefined
  const reach =
    leadView && core && agent && ctx.runnersLoaded && ctx.userId && ctx.platform !== undefined
      ? viewerReach(ctx.state, leadView.id, ctx.userId, {
          keeperId: core.ownerId,
          viewerId: ctx.userId,
          agent,
          pinnedRunner: runnerId,
          pinnedProfile:
            core.mind._tag === "runner" ? (core.mind.profileId ?? core.mind.requires.profile ?? null) : null,
          runners: reachRunnersOf(ctx.runners, ctx.platform),
          // decision 0166 §3.2 and §3.6: shared with this project counts; only what the viewer chose here
          projectId: ctx.projectId ?? ctx.state.project?.id ?? null,
          chosen: ctx.subscriptionChoice ?? null,
        })
      : null
  return {
    lead: leadView
      ? {
          name: leadView.name,
          state: leadView.state,
          reason: leadView.reason,
          mind: native ? "runner" : leadView.mind,
          runnerId,
          cli: native ? (nativeAgent ? cliName(nativeAgent) : null) : cliOfMind(core?.mind),
          ...(hosted ? { hosted: true } : {}),
        }
      : null,
    reach,
    lastTurnError: lastTurn?.error?.code ?? null,
    lastTurnRunnerId: lastTurn?.executor?._tag === "runner" ? lastTurn.executor.runnerId : null,
    lastTurnOn: lastTurn?.executor ? (lastTurn.executor._tag === "runner" ? "runner" : "cloud") : null,
    workspaceKey: workspaceKeyOf(core?.mind, ctx.modelKeys),
    runners: ctx.runnersLoaded
      ? {
          total: ctx.runners.length,
          online: ctx.runners.filter((r) => r.status === "online").length,
          onlineIds: ctx.runners.filter((r) => r.status === "online").map((r) => r.runnerId),
          // polish 2026-10-23 §5: asleep, not missing; her owner's runners first (the ones she thinks on)
          offlineIds: ctx.runners
            .filter((r) => r.status !== "online")
            .sort((a, b) => Number(b.ownerUserId === core?.ownerId) - Number(a.ownerUserId === core?.ownerId))
            .map((r) => r.runnerId),
        }
      : null,
    // onboarding week A6: items' runs only; a chat turn waiting is a message, said by the chat itself ("1 item is
    // waiting" read wrong under one's own message)
    waitingForRunner: ctx.state.runs.filter(
      (r) =>
        r.kind !== "turn" &&
        r.state._tag === "waiting" &&
        r.state.unmet.some((u) => u._tag === "no_runner_online" || u._tag === "runner_offline"),
    ).length,
    // wave O1: her turns wait because no online runner offers her coding tool ready (the cloud also reads profiles)
    leadWaitingForCli: leadView
      ? ctx.state.runs.filter(
          (r) =>
            isLiveTurnOf(r, leadView.id) &&
            r.state._tag === "waiting" &&
            r.state.unmet.some((u) => u._tag === "missing_agent" || u._tag === "profile_not_ready"),
        ).length
      : 0,
  }
}

/**
 * Decision 0166 §2: the viewer's reach, with the hub's word over it: a turn of theirs to the lead waiting on someone's
 * personal login (`owner_lock` with a profile) can't run, whatever the lists say, and names whose login it is.
 */
const viewerReach = (
  s: ProjectState,
  leadId: string,
  viewerId: string,
  input: Parameters<typeof leadReach>[0],
): LeadReach => {
  const reach = leadReach(input)
  for (const r of s.runs) {
    if (!isLiveTurnOf(r, leadId) || r.state._tag !== "waiting") continue
    const asker = r.askedBy?._tag === "user" ? r.askedBy.userId : r.ownerId
    if (asker !== viewerId) continue
    // decision 0166 §3.6: the hub says the viewer's own choice here left out what would run it
    if (r.state.unmet.some((u) => u._tag === "not_chosen"))
      return { canRun: false, lockedTo: null, lockedOn: null, notChosen: true }
    const lock = r.state.unmet.find((u) => u._tag === "owner_lock" && u.profileId !== undefined)
    if (lock?._tag === "owner_lock")
      return {
        canRun: false,
        lockedTo: reach.lockedTo ?? lock.ownerId ?? null,
        lockedOn: reach.lockedOn ?? (lock.platformRunner ? "subscription" : "machine"),
      }
  }
  return reach
}

const lastEndedTurn = (s: ProjectState, agentId: string): Run | null =>
  [...s.runs]
    .filter((r) => isLiveTurnOf(r, agentId) && r.state._tag === "ended")
    .sort((a, b) => (b.endedAt ?? b.requestedAt) - (a.endedAt ?? a.requestedAt))[0] ?? null

// ---- notices in the chat ----------------------------------------------------------------------------------------

/** The codes the cloud tags its notices with (`apps/cloud/src/agents/mind.ts` `NoticeCode`). */
export type NoticeCode =
  | "no_api_key"
  | "no_config"
  | "project_unreachable"
  | "previous_turn"
  | "turn_not_started"
  | "unsupported_mind"
  | "paused"
  | "turn_failed"
  | "rate_limited"
  | "tools_dropped"

/** Decision 0135: what happens after a failed turn (the cloud's `NoticeNext`). */
export type NoticeNext = "retrying_at" | "lead_decides" | "fix_setup" | "send_again"

export interface NoticeMeta {
  readonly kind: "setup" | "info"
  readonly code: string
  readonly detail?: string
  /** Decision 0135: how the turn ended, its error code, what happens next and when (`retrying_at`: epoch ms). */
  readonly stop?: string
  readonly errorCode?: string
  readonly next?: NoticeNext
  readonly at?: number
  /** Decision 0135: the run that failed (its chip on the notice line, decision 0144). */
  readonly runId?: string
  /** Decision 0155: a `rate_limited` notice's limit that ran out (`5h`, `24h`, `7d`), when known. */
  readonly limit?: string
  /** Decision 0168: where the failed turn ran (from its run, filled in by the page), for what failed. */
  readonly where?: "platform" | "runner" | null
  /** Decision 0168: this `turn_not_started` follows a failed turn Minga said it would try again: the retry didn't start. */
  readonly afterRetry?: boolean
}

/**
 * Whether the banner covers a setup notice: `no_api_key` ↔ a `no_key` problem shown; decision 0135: a failed turn to
 * fix in settings ↔ the banner's key, credit or sign-in problem. Otherwise the notice renders.
 */
export const noticeCovered = (
  code: string,
  problems: ReadonlyArray<SetupProblem>,
  notice?: Pick<NoticeMeta, "next" | "errorCode">,
): boolean => {
  if (code === "no_api_key") return problems.some((p) => p._tag === "no_key")
  if (notice?.next !== "fix_setup") return false
  if (notice.errorCode === "credit_exhausted") return problems.some((p) => p._tag === "no_credit")
  return problems.some((p) => p._tag === "key_refused" || p._tag === "no_key" || p._tag === "cli_signed_out")
}

const NEXT_WORDS: Readonly<Record<NoticeNext, (lead: string, at: number | null) => string>> = {
  retrying_at: (_lead, at) =>
    at !== null ? `Minga tries again at ${clockTime(at)}.` : "Minga tries again shortly.",
  lead_decides: (lead) => `${lead} sees it in the next turn.`,
  fix_setup: () => "Fix the setting, then send again.",
  send_again: () => "Send the message again to retry.",
}

const STOP_WHY: Readonly<Record<string, string>> = {
  cli_error: "it hit an error",
  context_exhausted: "it ran out of context",
  max_turns: "it reached its step limit",
  policy_refused: "the runner refused it",
  permission_denied: "a permission it needed was denied",
  rate_limited: "the model is rate limited",
  budget_exhausted: "the model account is out of credit",
  cancelled: "it was cancelled before it started",
  no_progress: "it made no progress for a long time",
}

/**
 * Decision 0135, 0168: what failed in a turn, in plain words, the same in the chat's notice, the line above the composer
 * and the screen reader's status. `where`: where the turn ran (a sandbox on the Platform Runner, a paired runner);
 * unknown reads as the Platform Runner's words for a sandbox that didn't start.
 */
export const turnFailureWhy = (
  n: { readonly stop?: string | null | undefined; readonly errorCode?: string | null | undefined },
  where: "platform" | "runner" | null = null,
): string =>
  n.errorCode === "credit_exhausted"
    ? "the model account is out of credit"
    : n.stop === "auth_error"
      ? "a key or sign-in was refused"
      : n.stop === "runner_lost"
        ? "the runner was lost"
        : n.stop === "sandbox_error"
          ? // decision 0168: a paired runner prepares a checkout; the Platform Runner starts a sandbox
            where === "runner"
            ? "its runner couldn't prepare the checkout"
            : "its sandbox didn't start"
          : n.stop === "mcp_unavailable"
            ? "Minga's tools didn't connect"
            : n.stop === "wall_clock"
              ? "it ran past its time limit"
              : n.errorCode && /^http_5\d\d$|overloaded/.test(n.errorCode)
                ? "the model provider was overloaded"
                : (STOP_WHY[n.stop ?? ""] ?? "it stopped early")

/** Decision 0135: a failed turn's line from its stop reason and what happens next. */
const failedTurnWords = (
  n: Pick<NoticeMeta, "stop" | "errorCode" | "next" | "at" | "where">,
  leadName: string,
): string => {
  const why = turnFailureWhy(n, n.where ?? null)
  const next = n.next ? ` ${NEXT_WORDS[n.next](leadName, n.at ?? null)}` : ""
  return `${leadName}'s turn failed: ${why}.${next}`
}

/** The quiet line for a notice, from its code (never the stored text); unknown code → "Ada's turn didn't run." */
/** Decision 0155: a usage limit in words ("5-hour limit"); the same words as core `limitWindowWords`. */
const limitWords = (limit: string | undefined): string =>
  limit === "5h"
    ? "5-hour limit"
    : limit === "7d"
      ? "weekly limit"
      : limit === "24h"
        ? "daily limit"
        : "usage limit"

export const noticeWords = (
  code: string,
  detail: string | null,
  leadName: string,
  more: Pick<NoticeMeta, "stop" | "errorCode" | "next" | "at" | "limit" | "where" | "afterRetry"> = {},
): string => {
  switch (code) {
    case "rate_limited": {
      // incident 2026-10-15: `detail` is the reset (epoch ms), shown in the viewer's clock
      const at = detail !== null && /^\d+$/.test(detail) ? clockTime(Number(detail)) : null
      // decision 0135: retried at the reset
      // decision 0155: in one line, with the limit that ran out
      if (more.next === "retrying_at" && at)
        return `Paused until ${at} for the subscription's ${limitWords(more.limit)}; ${leadName} continues then.`
      return `${at ? `Rate limited until ${at}` : "Rate limited for now"}: write again after that, or change ${leadName}'s mind on ${leadName}'s page.`
    }
    case "tools_dropped":
      return `Some tools were left out of ${leadName}'s turn (the model provider would refuse them)${detail ? `: ${detail.split(",").join(", ")}` : ""}.`
    case "no_api_key":
      return `${leadName} has no model key: add one in Settings › Model providers.`
    case "no_config":
      return `${leadName} isn't ready yet: the project is still being set up.`
    case "project_unreachable":
      return `${leadName}'s turn didn't start: the project couldn't be reached. It starts again by itself.`
    case "previous_turn":
      return `${leadName} is still on the last turn; this one starts after it.`
    case "turn_not_started":
      // decision 0168: says what happens next (it never starts by itself), and after a retry that the retry didn't
      return more.afterRetry === true
        ? `${leadName} couldn't try again: the turn didn't start. Send your message again to retry.`
        : `${leadName}'s turn didn't start. Send your message again to retry.`
    case "unsupported_mind":
      return `${leadName} can't run here yet: pick where ${leadName} runs in Settings › ${leadName}.`
    case "paused":
      return `${leadName} is paused. Your message waits until ${leadName} is resumed.`
    case "turn_failed":
      // decision 0135: a notice that names its stop says how the turn ended and what happens next
      if (more.stop !== undefined) return failedTurnWords(more, leadName)
      return `${leadName}'s turn failed before it started. Send the message again to retry.`
    default:
      return `${leadName}'s turn didn't run.`
  }
}

const LEGACY: ReadonlyArray<readonly [RegExp, NoticeCode]> = [
  [/^No (Anthropic|OpenAI) key in this workspace\./, "no_api_key"],
  [/^This agent is not set up yet/, "no_config"],
  [/^The project could not be reached, so this turn did not start yet/, "project_unreachable"],
  [/^The previous turn is still running/, "previous_turn"],
  [/^The turn was not started \(/, "turn_not_started"],
  [/^This agent's mind is not supported yet/, "unsupported_mind"],
  [/^@\S+ is paused\. Your message waits/, "paused"],
  [
    /^The turn (failed before the model was called|could not start \(the project refused it\))/,
    "turn_failed",
  ],
]

/**
 * The notice an assistant message is, from its metadata (`{ notice: { kind, code } }`), or for untagged messages from
 * before wave B2, from its whole text. Null for an ordinary reply.
 */
export const noticeOf = (metadata: unknown, text: string): NoticeMeta | null => {
  const n = (metadata as { notice?: unknown } | null | undefined)?.notice as Partial<NoticeMeta> | undefined
  if (n && (n.kind === "setup" || n.kind === "info") && typeof n.code === "string")
    return {
      kind: n.kind,
      code: n.code,
      ...(typeof n.detail === "string" ? { detail: n.detail } : {}),
      ...(typeof n.stop === "string" ? { stop: n.stop } : {}),
      ...(typeof n.errorCode === "string" ? { errorCode: n.errorCode } : {}),
      ...(typeof n.next === "string" && n.next in NEXT_WORDS ? { next: n.next } : {}),
      ...(typeof n.at === "number" ? { at: n.at } : {}),
      ...(typeof n.runId === "string" ? { runId: n.runId } : {}),
    }
  const t = text.trim()
  const hit = LEGACY.find(([re]) => re.test(t))
  if (!hit) return null
  return { kind: hit[1] === "no_api_key" ? "setup" : "info", code: hit[1] }
}

/**
 * Decision 0168: where a failed turn ran, for what failed ("its sandbox didn't start" on the Platform Runner, "its runner
 * couldn't prepare the checkout" on a paired one). Null when unknown (no run, or the Platform Runner not loaded yet).
 */
export const failureWhere = (
  run: Pick<Run, "executor"> | undefined,
  platformRunnerId: string | null | undefined,
): "platform" | "runner" | null =>
  run?.executor?._tag !== "runner" || platformRunnerId === undefined
    ? null
    : run.executor.runnerId === platformRunnerId
      ? "platform"
      : "runner"
